import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  SearchFieldCounterConfig,
  SubItemType,
} from "@shared/components/application/form/FormField/types";
import { calculateCounters } from "@shared/components/application/form/FormField/SearchFieldShared.web";
import useSearchWithFetchOptions from "./useSearchWithFetchOptions";
import type { SearchItemBase, SearchDataSource } from "./search-types";

export interface UseSearchSharedStateConfig<TItem extends SearchItemBase> {
  /** Result from the service hook (useSearchEvent, etc.) */
  serviceResult: SearchDataSource;
  /** Simple async fetch function (non-paginated). Used when no service is provided. */
  fetchOptions?: (query: string) => Promise<TItem[]>;
  /** Whether a service was provided (used to determine fetchOptions vs service) */
  service?: unknown;
  /** Debounced search query from the component */
  debouncedQuery: string;
  single?: boolean;
  /** Controlled value. Array for multi-select, single item (or null) for single-select. */
  value?: TItem[] | TItem | null;
  onChange?: (items: TItem[]) => void;
  onSelect?: (item: TItem | null) => void;
  onRemove?: (item: TItem) => void;
  filterOptions?: (item: TItem) => boolean;
  withSubItems?: boolean;
  subItemsKey?: string;
  counters?: SearchFieldCounterConfig[];
  getFormValue?: (items: TItem[], subs?: Map<number, number[]>) => unknown;
  selectedItemsDisplay?: "tags" | "cards" | "list";
  showSelectedBelow?: boolean;
}

export interface UseSearchSharedStateResult<TItem extends SearchItemBase> {
  selectedSet: TItem[];
  filteredItems: TItem[];
  counterItems: Array<{ label: string; count: number }>;
  subItemSelections: Map<number, number[]>;
  displayMode: "tags" | "cards" | "list";
  handleAdd: (item: TItem) => void;
  handleRemove: (itemId: number) => void;
  handleSingleSelect: (item: TItem | null) => void;
  handleToggleSubItem: (
    itemId: number,
    subItemId: number,
    isSelected: boolean
  ) => void;
  isFetching: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  showSelectedBelow: boolean;
  withSubItems: boolean;
  subItemsKey: string;
  single?: boolean;
  /** The single-select value derived from the unified `value` prop (undefined when multi-select) */
  singleValue?: TItem | null;
}

export default function useSearchSharedState<TItem extends SearchItemBase>({
  serviceResult,
  fetchOptions,
  service,
  debouncedQuery,
  single,
  value,
  onChange,
  onSelect,
  onRemove,
  filterOptions,
  withSubItems = false,
  subItemsKey = "",
  counters,
  getFormValue,
  selectedItemsDisplay = "cards",
  showSelectedBelow = false,
}: UseSearchSharedStateConfig<TItem>): UseSearchSharedStateResult<TItem> {
  // Derive single vs multi value from the unified `value` prop
  const singleValue =
    single && value !== undefined && !Array.isArray(value)
      ? (value as TItem | null)
      : undefined;
  const multiValue = !single && Array.isArray(value) ? value : undefined;

  // Determine data source: fetchOptions (non-paginated) vs service (paginated)
  const useFetchOpts = Boolean(fetchOptions && !service);

  const fetchOptionsResult = useSearchWithFetchOptions<TItem>({
    fetchOptions:
      fetchOptions ??
      ((() => Promise.resolve([])) as (query: string) => Promise<TItem[]>),
    debouncedQuery,
  });

  const { items, isFetching, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useFetchOpts ? fetchOptionsResult : serviceResult;

  // Controlled / uncontrolled selected set (multi-select)
  const isControlled = multiValue !== undefined;
  const [internalSelectedSet, setInternalSelectedSet] = useState<TItem[]>([]);
  const selectedSet = (
    isControlled ? multiValue : internalSelectedSet
  ) as TItem[];
  const selectedIds = useMemo(
    () => selectedSet.map((item) => item.id),
    [selectedSet]
  );

  // Sub-item selections (e.g. ticket types, product attributes, membership levels)
  const [subItemSelections, setSubItemSelections] = useState<
    Map<number, number[]>
  >(new Map());

  useEffect(() => {
    const next = new Map<number, number[]>();
    selectedSet.forEach((item) => {
      const subItems =
        (item[subItemsKey as keyof TItem] as SubItemType[] | undefined) ?? [];
      if (!subItemSelections.has(item.id)) {
        next.set(
          item.id,
          subItems.map((s) => s.id)
        );
      } else {
        const existing = subItemSelections.get(item.id);
        if (existing) next.set(item.id, existing);
      }
    });
    setSubItemSelections(next);
  }, [selectedIds.join(","), subItemsKey]);

  // Filter items: exclude selected + apply consumer filterOptions
  const filteredItems = useMemo(() => {
    let list = (items as TItem[]).filter(
      (item) => !selectedIds.includes(item.id)
    );
    if (filterOptions) {
      list = list.filter((i) => filterOptions(i));
    }
    return list;
  }, [items, selectedIds, filterOptions]);

  // Delegate onChange, optionally transforming via getFormValue
  const fireOnChange = useCallback(
    (newSet: TItem[], subs?: Map<number, number[]>) => {
      if (!onChange) return;
      if (getFormValue && withSubItems && subs) {
        (onChange as (v: unknown) => void)(getFormValue(newSet, subs));
      } else if (getFormValue) {
        (onChange as (v: unknown) => void)(getFormValue(newSet, undefined));
      } else {
        onChange(newSet);
      }
    },
    [onChange, getFormValue, withSubItems]
  );

  const handleAdd = useCallback(
    (item: TItem) => {
      const newSet = single ? [item] : [...selectedSet, item];
      if (!isControlled) {
        setInternalSelectedSet(newSet);
      }

      if (withSubItems) {
        const subItems =
          (item[subItemsKey as keyof TItem] as SubItemType[] | undefined) ?? [];
        const nextSubs = new Map(subItemSelections);
        nextSubs.set(
          item.id,
          subItems.map((s) => s.id)
        );
        setSubItemSelections(nextSubs);
        fireOnChange(newSet, nextSubs);
      } else {
        fireOnChange(newSet);
      }
    },
    [
      isControlled,
      single,
      selectedSet,
      withSubItems,
      subItemsKey,
      subItemSelections,
      fireOnChange,
    ]
  );

  const handleRemove = useCallback(
    (itemId: number) => {
      const item = selectedSet.find((i) => i.id === itemId);
      const newSet = selectedSet.filter((i) => i.id !== itemId);
      if (!isControlled) {
        setInternalSelectedSet(newSet);
      }
      const nextSubs = new Map(subItemSelections);
      nextSubs.delete(itemId);
      setSubItemSelections(nextSubs);
      fireOnChange(newSet, withSubItems ? nextSubs : undefined);
      if (onRemove && item) {
        onRemove(item);
      }
    },
    [
      isControlled,
      selectedSet,
      onRemove,
      withSubItems,
      subItemSelections,
      fireOnChange,
    ]
  );

  const handleSingleSelect = useCallback(
    (item: TItem | null) => {
      if (onSelect) {
        onSelect(item);
      }
    },
    [onSelect]
  );

  const handleToggleSubItem = useCallback(
    (itemId: number, subItemId: number, isSelected: boolean) => {
      const next = new Map(subItemSelections);
      const current = next.get(itemId) ?? [];
      const nextIds = isSelected
        ? [...current, subItemId]
        : current.filter((id) => id !== subItemId);
      next.set(itemId, nextIds);
      setSubItemSelections(next);
      if (onChange && getFormValue && withSubItems) {
        (onChange as (v: unknown) => void)(getFormValue(selectedSet, next));
      }
    },
    [selectedSet, subItemSelections, onChange, getFormValue, withSubItems]
  );

  const counterItems = calculateCounters(
    counters,
    selectedSet,
    withSubItems ? subItemSelections : undefined
  );

  return {
    selectedSet,
    filteredItems,
    counterItems,
    subItemSelections,
    displayMode: selectedItemsDisplay,
    handleAdd,
    handleRemove,
    handleSingleSelect,
    handleToggleSubItem,
    isFetching,
    hasNextPage: Boolean(hasNextPage),
    fetchNextPage,
    isFetchingNextPage,
    showSelectedBelow,
    withSubItems,
    subItemsKey,
    single,
    singleValue,
  };
}
